import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import LanguageSelector from "./LanguageSelector";
import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const PRESALE_DATE = new Date("2026-05-01T10:00:00");
const PRESALE_END_DATE = new Date("2026-12-31T23:59:59");

export default function NeokittyBuy() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";

  const [now, setNow] = useState(new Date());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState(lang);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [showPopup, setShowPopup] = useState(false);
/* === Presale order form state === */
const [orderEmail, setOrderEmail] = useState("");
const [orderWallet, setOrderWallet] = useState("");
const [orderTxHash, setOrderTxHash] = useState("");
const [orderEthAmount, setOrderEthAmount] = useState("");
const [orderConsent, setOrderConsent] = useState(false);
const [orderStatus, setOrderStatus] = useState("idle");
const [orderPopup, setOrderPopup] = useState(null);
const [notifyPopup, setNotifyPopup] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setLanguage(lang);
  }, [lang]);

  const isPresaleLive = now >= PRESALE_DATE && now <= PRESALE_END_DATE;
  const isPresaleClosed = now > PRESALE_END_DATE;
  const isPreLive = now < PRESALE_DATE;

  const diffToStart = PRESALE_DATE - now;
  const diffToEnd = PRESALE_END_DATE - now;

  const countdownStart = useMemo(() => {
    if (diffToStart <= 0) return null;
    return {
      days: Math.floor(diffToStart / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diffToStart / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diffToStart / (1000 * 60)) % 60),
      seconds: Math.floor((diffToStart / 1000) % 60),
    };
  }, [diffToStart]);

  const countdownEnd = useMemo(() => {
    if (diffToEnd <= 0) return null;
    return {
      days: Math.floor(diffToEnd / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diffToEnd / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diffToEnd / (1000 * 60)) % 60),
      seconds: Math.floor((diffToEnd / 1000) % 60),
    };
  }, [diffToEnd]);

  const texts = {
    en: {
      titlePre: "PRESALE STARTS IN",
      titleLive: "PRESALE OPEN",
      titleClosed: "PRESALE CLOSED",
      subtitlePre: "Leave your details and we will notify you when the presale opens.",
      subtitleLive: "You still have time to participate in the NeoKitty presale.",
      subtitleClosed: "The presale has ended. Thank you for participating.",
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      seconds: "Seconds",
      name: "Name",
      email: "Email",
      language: "Language",
      consent:
        "I agree to receive emails related to the NeoKitty presale. My data will only be used for this purpose.",
      notify: "Notify me",
      back: "Back to Home",
      success: "Form submitted successfully. We will notify you by email.",
      instructionsTitle: "Presale Instructions",
      network: "Network: Base Mainnet",
      wallet: "Send ETH to the following address:",
      address: "0x3B785597D43F0EF8A326EdD8c96468e3E7E8625E",
      calc:
        "40% of the total NKTT supply will be distributed proportionally among all ETH received during the presale.",
      openUntil: "Presale open until:",
    },
    es: {
      titlePre: "LA PREVENTA EMPIEZA EN",
      titleLive: "PREVENTA ABIERTA",
      titleClosed: "PREVENTA CERRADA",
      subtitlePre: "Déjanos tus datos y te avisaremos cuando comience la preventa.",
      subtitleLive: "Todavía estás a tiempo de participar en la preventa de NeoKitty.",
      subtitleClosed: "La preventa ha finalizado. Gracias por participar.",
      days: "Días",
      hours: "Horas",
      minutes: "Minutos",
      seconds: "Segundos",
      name: "Nombre",
      email: "Correo electrónico",
      language: "Idioma",
      consent:
        "Acepto recibir correos relacionados con la preventa de NeoKitty. Mis datos solo se usarán para este fin.",
      notify: "Avisarme",
      back: "Volver atrás",
      success: "Formulario enviado correctamente. Te avisaremos por correo.",
      instructionsTitle: "Instrucciones de preventa",
      network: "Red: Base Mainnet",
      wallet: "Envía ETH a la siguiente dirección:",
      address: "0x3B785597D43F0EF8A326EdD8c96468e3E7E8625E",
      calc:
        "El 40% del suministro total de NKTT se repartirá proporcionalmente entre todo el ETH recibido.",
      openUntil: "Preventa abierta hasta:",
    },
    ru: {
      titlePre: "ПРЕСЕЙЛ НАЧНЁТСЯ ЧЕРЕЗ",
      titleLive: "ПРЕСЕЙЛ ОТКРЫТ",
      titleClosed: "ПРЕСЕЙЛ ЗАКРЫТ",
      subtitlePre: "Оставьте данные, и мы уведомим вас о начале пресейла.",
      subtitleLive: "У вас ещё есть время принять участие в пресейле NeoKitty.",
      subtitleClosed: "Пресейл завершён. Спасибо за участие.",
      days: "Дни",
      hours: "Часы",
      minutes: "Минуты",
      seconds: "Секунды",
      name: "Имя",
      email: "Email",
      language: "Язык",
      consent:
        "Я согласен получать письма, связанные с пресейлом NeoKitty. Данные используются только для этого.",
      notify: "Уведомить",
      back: "Назад",
      success: "Форма успешно отправлена. Мы уведомим вас по email.",
      instructionsTitle: "Инструкции пресейла",
      network: "Сеть: Base Mainnet",
      wallet: "Отправьте ETH на адрес:",
      address: "0x3B785597D43F0EF8A326EdD8c96468e3E7E8625E",
      calc:
        "40% от общего объёма NKTT будет распределено пропорционально полученному ETH.",
      openUntil: "Пресейл открыт до:",
    },
    zh: {
      titlePre: "预售倒计时",
      titleLive: "预售进行中",
      titleClosed: "预售已结束",
      subtitlePre: "填写信息，我们将在预售开始时通知你。",
      subtitleLive: "你仍然可以参与 NeoKitty 的预售。",
      subtitleClosed: "预售已结束，感谢参与。",
      days: "天",
      hours: "小时",
      minutes: "分钟",
      seconds: "秒",
      name: "姓名",
      email: "邮箱",
      language: "语言",
      consent:
        "我同意接收与 NeoKitty 预售相关的邮件。数据仅用于此目的。",
      notify: "通知我",
      back: "返回",
      success: "表单提交成功，我们会通过邮件通知你。",
      instructionsTitle: "预售说明",
      network: "网络：Base 主网",
      wallet: "请将 ETH 发送到以下地址：",
      address: "0x3B785597D43F0EF8A326EdD8c96468e3E7E8625E",
      calc:
        "40% 的 NKTT 总量将按收到的 ETH 比例分配。",
      openUntil: "预售截止至：",
    },
  };

  const t = texts[lang];
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = name && validEmail && consent && status !== "loading";

  const submit = async (e) => {
  e.preventDefault();
  if (!canSubmit) return;

  setStatus("loading");

  const { error } = await supabase.from("presale_notify").insert([
    { name, email, language, consent: true },
  ]);

  if (error) {
    if (error.code === "23505") {
      setNotifyPopup("duplicate");
    } else {
      setNotifyPopup("error");
    }

    setStatus("idle");
    return;
  }

  setNotifyPopup("success");
  setStatus("idle");
};
/* === Presale order validations === */
const validOrderEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orderEmail);
const validOrderWallet = orderWallet.trim().length > 0;
const validOrderTxHash = orderTxHash.trim().length > 0;
const validOrderEthAmount =
  !isNaN(orderEthAmount) && Number(orderEthAmount) > 0;

const canSubmitOrder =
  validOrderEmail &&
  validOrderWallet &&
  validOrderTxHash &&
  validOrderEthAmount &&
  orderConsent &&
  orderStatus !== "loading";


/* === Submit presale order === */
const submitOrder = async (e) => {
  e.preventDefault();
  if (!canSubmitOrder) return;

  setOrderStatus("loading");

 const { error } = await supabase
  .from("presale_orders")
  .insert([
    {
      email: orderEmail,
      wallet: orderWallet,
      tx_hash: orderTxHash,
      eth_amount: Number(orderEthAmount),
      language: lang,
      consent: true,
    },
  ]);


if (error) {
  if (error.code === "23505") {
    setOrderPopup("duplicate");
  } else {
    setOrderPopup("error");
  }

  setOrderStatus("idle"); // ← AÑADIR
  return;
}

setOrderPopup("success");
setOrderStatus("idle"); // ← AÑADIR


};

  const backToHome = lang === "en" ? "/" : `/${lang}`;

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>NeoKitty Presale</title>
      </Helmet>
{notifyPopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
    <div className="bg-[#041a30] border border-white/20 rounded-2xl p-6 max-w-sm text-center relative">
      <button
        onClick={() => {
          // ÉXITO → limpiar todo
          if (notifyPopup === "success") {
            setName("");
            setEmail("");
            setConsent(false);
          }

          // DUPLICADO → limpiar solo email
          if (notifyPopup === "duplicate") {
            setEmail("");
          }

          setNotifyPopup(null);
        }}
        className="absolute top-3 right-3 text-white/60 hover:text-white"
      >
        ✕
      </button>

      {notifyPopup === "success" && (
        <p className="text-green-400">
          {lang === "es" && "Te avisaremos cuando comience la preventa"}
          {lang === "en" && "We will notify you when the presale starts"}
          {lang === "ru" && "Мы уведомим вас о начале пресейла"}
          {lang === "zh" && "预售开始时我们会通知你"}
        </p>
      )}

      {notifyPopup === "duplicate" && (
        <p className="text-yellow-400">
          {lang === "es" && "Este email ya está registrado"}
          {lang === "en" && "This email is already registered"}
          {lang === "ru" && "Этот email уже зарегистрирован"}
          {lang === "zh" && "该邮箱已被登记"}
        </p>
      )}

      {notifyPopup === "error" && (
        <p className="text-red-400">
          {lang === "es" && "Error al enviar el formulario"}
          {lang === "en" && "Error submitting the form"}
          {lang === "ru" && "Ошибка при отправке формы"}
          {lang === "zh" && "提交表单时出错"}
        </p>
      )}
    </div>
  </div>
)}

     {orderPopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
    <div className="bg-[#041a30] border border-white/20 rounded-2xl p-6 max-w-sm text-center relative">
    <button
  onClick={() => {
    // CASO 1: ÉXITO → limpiar TODO
    if (orderPopup === "success") {
      setOrderEmail("");
      setOrderWallet("");
      setOrderTxHash("");
      setOrderEthAmount("");
      setOrderConsent(false);
    }

    // CASO 2: DUPLICADO → limpiar SOLO el hash
    if (orderPopup === "duplicate") {
      setOrderTxHash("");
    }

    // cerrar popup
    setOrderPopup(null);
  }}
  className="absolute top-3 right-3 text-white/60 hover:text-white"
>
  ✕
</button>

      {orderPopup === "success" && (
  <p className="text-green-400">
    {lang === "es" && "Transacción registrada correctamente"}
    {lang === "en" && "Transaction registered successfully"}
    {lang === "ru" && "Транзакция успешно зарегистрирована"}
    {lang === "zh" && "交易已成功登记"}
  </p>
)}

{orderPopup === "duplicate" && (
  <p className="text-yellow-400">
    {lang === "es" && "Esta transacción ya fue registrada"}
    {lang === "en" && "This transaction was already registered"}
    {lang === "ru" && "Эта транзакция уже была зарегистрирована"}
    {lang === "zh" && "该交易已被登记"}
  </p>
)}

{orderPopup === "error" && (
  <p className="text-red-400">
    {lang === "es" && "Error al registrar la transacción"}
    {lang === "en" && "Error registering the transaction"}
    {lang === "ru" && "Ошибка при регистрации транзакции"}
    {lang === "zh" && "交易登记时出错"}
  </p>
)}
    </div>
  </div>
)}

      <div className="min-h-screen bg-gradient-to-b from-[#071029] via-[#071a3a] to-[#04233f] text-white antialiased">
        <LanguageSelector />

        <main className="max-w-4xl mx-auto px-6 py-24 text-center">
          <img
            src="/assets/logo-neokitty.png"
            alt="NeoKitty"
            className="w-40 mx-auto mb-8 drop-shadow-[0_0_16px_#00E6FF70]"
          />

          <h1 className="text-4xl md:text-5xl font-extrabold mb-6">
            {isPreLive && t.titlePre}
            {isPresaleLive && t.titleLive}
            {isPresaleClosed && t.titleClosed}
          </h1>

          {isPreLive && countdownStart && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              {["days", "hours", "minutes", "seconds"].map((k) => (
                <div key={k} className="bg-white/5 rounded-xl p-4">
                  <div className="text-3xl font-bold">{countdownStart[k]}</div>
                  <div className="text-xs text-slate-300">{t[k]}</div>
                </div>
              ))}
            </div>
          )}

          {isPresaleLive && countdownEnd && (
            <>
              <p className="text-slate-300 mb-4">{t.subtitleLive}</p>
              <p className="text-slate-400 mb-6">
                {t.openUntil} {PRESALE_END_DATE.toLocaleDateString()}
              </p>
              <div className="grid grid-cols-4 gap-4 mb-10">
                {["days", "hours", "minutes", "seconds"].map((k) => (
                  <div key={k} className="bg-white/5 rounded-xl p-4">
                    <div className="text-3xl font-bold">{countdownEnd[k]}</div>
                    <div className="text-xs text-slate-300">{t[k]}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white/5 p-6 rounded-2xl text-left shadow-[0_0_20px_#00E6FF30]">
  <h2 className="text-xl font-bold mb-4">
    {lang === "es" && "Instrucciones de preventa"}
    {lang === "en" && "Presale Instructions"}
    {lang === "ru" && "Инструкции пресейла"}
    {lang === "zh" && "预售说明"}
  </h2>

  <p className="mb-2">
    {lang === "es" &&
      "Red: Base Mainnet. Asegúrate de que tu wallet esté configurada en la red Base antes de enviar ETH."}
    {lang === "en" &&
      "Network: Base Mainnet. Make sure your wallet is set to the Base network before sending ETH."}
    {lang === "ru" &&
      "Сеть: Base Mainnet. Перед отправкой ETH убедитесь, что ваш кошелёк подключён к сети Base."}
    {lang === "zh" &&
      "网络：Base 主网。发送 ETH 前请确认你的钱包已连接到 Base 网络。"}
  </p>

  <p className="mb-2">
    {lang === "es" &&
      "Envía ETH a la siguiente dirección desde tu wallet. No necesitas añadir ningún concepto ni dato adicional."}
    {lang === "en" &&
      "Send ETH to the following address from your wallet. No memo or extra data is required."}
    {lang === "ru" &&
      "Отправьте ETH на следующий адрес со своего кошелька. Дополнительные данные указывать не нужно."}
    {lang === "zh" &&
      "请从你的钱包向以下地址发送 ETH，无需填写任何附加信息。"}
  </p>

  <p className="font-mono break-all text-[#00E6FF] mb-4">
    0x3B785597D43F0EF8A326EdD8c96468e3E7E8625E
  </p>

  <p className="mb-4 text-slate-300">
    {lang === "es" && (
      <>
        Para comprobar tu envío, entra en{" "}
        <a
          href="https://basescan.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline hover:text-blue-300"
        >
          https://basescan.org
        </a>{" "}
        y pega la dirección de tu propia billetera en el buscador. Verás todas tus
        transacciones. La que tenga la hora más reciente y el importe en ETH que
        acabas de enviar es tu transacción de la preventa.
      </>
    )}

    {lang === "en" && (
      <>
        To check your transaction, go to{" "}
        <a
          href="https://basescan.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline hover:text-blue-300"
        >
          https://basescan.org
        </a>{" "}
        and paste your own wallet address into the search bar. You will see all
        your transactions. The most recent one with the correct ETH amount is
        your presale transaction.
      </>
    )}

    {lang === "ru" && (
      <>
        Чтобы проверить перевод, зайдите на{" "}
        <a
          href="https://basescan.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline hover:text-blue-300"
        >
          https://basescan.org
        </a>{" "}
        и вставьте адрес своего кошелька в поиск. Самая новая транзакция по
        времени и с нужной суммой ETH — это ваш перевод на пресейл.
      </>
    )}

    {lang === "zh" && (
      <>
        要查看你的交易，请访问{" "}
        <a
          href="https://basescan.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline hover:text-blue-300"
        >
          https://basescan.org
        </a>{" "}
        并在搜索框中输入你自己的钱包地址。最新的一笔且金额正确的交易就是你的预售交易。
      </>
    )}
  </p>

  <div className="mt-6 pt-4 border-t border-white/10 text-slate-300">
    {lang === "es" &&
      "El 40% del suministro total de NKTT se repartirá proporcionalmente entre todo el ETH recibido durante la preventa."}
    {lang === "en" &&
      "40% of the total NKTT supply will be distributed proportionally among all ETH received during the presale."}
    {lang === "ru" &&
      "40% от общего объёма NKTT будет распределено пропорционально полученному ETH."}
    {lang === "zh" &&
      "NKTT 总供应量的 40% 将根据收到的 ETH 比例进行分配。"}
  </div>
</div>
<p className="mt-6 mb-4 text-center font-semibold text-white">
  {lang === "es" && "Completa el siguiente formulario para recibir tus NKTT"}
  {lang === "en" && "Complete the form below to receive your NKTT"}
  {lang === "ru" && "Заполните форму ниже, чтобы получить свои NKTT"}
  {lang === "zh" && "请填写以下表格以接收你的 NKTT"}
</p>

<form
  onSubmit={submitOrder}
  className="mt-10 max-w-md mx-auto bg-white/5 p-8 rounded-2xl shadow-[0_0_20px_#00E6FF30] text-left"
>
  {/* Email */}
  <label className="block mb-4">
    <span className="text-sm">
      {lang === "es" && "Correo electrónico"}
      {lang === "en" && "Email"}
      {lang === "ru" && "Email"}
      {lang === "zh" && "邮箱"}
    </span>
    <input
      type="email"
      value={orderEmail}
      onChange={(e) => setOrderEmail(e.target.value)}
      className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2"
    />
  </label>
{/* Language */}
<label className="block mb-4">
  <span className="text-sm">
    {lang === "es" && "Idioma"}
    {lang === "en" && "Language"}
    {lang === "ru" && "Язык"}
    {lang === "zh" && "语言"}
  </span>

  <select
    value={lang}
    onChange={(e) => {
      i18n.changeLanguage(e.target.value);
    }}
    className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2"
  >
    <option value="en">English</option>
    <option value="es">Español</option>
    <option value="ru">Русский</option>
    <option value="zh">中文</option>
  </select>
</label>

  {/* Wallet */}
  <label className="block mb-4">
    <span className="text-sm">
      {lang === "es" && "Dirección de tu wallet"}
      {lang === "en" && "Your wallet address"}
      {lang === "ru" && "Адрес вашего кошелька"}
      {lang === "zh" && "你的钱包地址"}
    </span>
<input
  type="text"
  placeholder="0x..."
  value={orderWallet}
  onChange={(e) => {
    const value = e.target.value.trim();

    // Permitir solo formato de wallet EVM
    if (
      value === "" ||
      (/^0x[a-fA-F0-9]*$/.test(value) && value.length <= 42)
    ) {
      setOrderWallet(value);
    }
  }}
  className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2 font-mono"
/>

  </label>

  {/* Transaction hash */}
  <label className="block mb-4">
    <span className="text-sm">
      {lang === "es" && "Hash de la transacción"}
      {lang === "en" && "Transaction hash"}
      {lang === "ru" && "Хеш транзакции"}
      {lang === "zh" && "交易哈希"}
    </span>
<input
  type="text"
  placeholder="0x..."
  value={orderTxHash}
  onChange={(e) => {
    const value = e.target.value.trim();

    // Permitir solo formato de hash EVM
    if (
      value === "" ||
      /^0x[a-fA-F0-9]*$/.test(value) && value.length <= 66
    ) {
      setOrderTxHash(value);
    }
  }}
  className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2 font-mono"
/>

  </label>

  {/* ETH amount */}
  <label className="block mb-4">
    <span className="text-sm">
      {lang === "es" && "Cantidad enviada en ETH"}
      {lang === "en" && "ETH amount sent"}
      {lang === "ru" && "Отправленная сумма ETH"}
      {lang === "zh" && "发送的 ETH 数量"}
    </span>
<input
  type="text"
  inputMode="decimal"
  placeholder="0.0"
  value={orderEthAmount}
  onChange={(e) => {
    const value = e.target.value;

    // Permite solo números y un único punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      setOrderEthAmount(value);
    }
  }}
  className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2"
/>
  </label>

  {/* Consent */}
  <label className="flex items-start gap-3 mb-6 text-sm">
    <input
      type="checkbox"
      checked={orderConsent}
      onChange={(e) => setOrderConsent(e.target.checked)}
      className="mt-1"
    />
    <span>
      {lang === "es" &&
        "Acepto que mis datos se utilicen únicamente para registrar mi participación en la preventa."}
      {lang === "en" &&
        "I agree that my data will be used only to register my participation in the presale."}
      {lang === "ru" &&
        "Я согласен на использование моих данных исключительно для регистрации участия в пресейле."}
      {lang === "zh" &&
        "我同意仅将我的数据用于登记我参与预售。"}
    </span>
  </label>

  {/* Submit */}
  <button
    type="submit"
    className={`w-full py-3 rounded-xl font-bold ${
      canSubmitOrder
        ? "bg-[#00E6FF] text-[#022034]"
        : "bg-gray-600 text-gray-300 cursor-not-allowed"
    }`}
  >
    {lang === "es" && "Registrar transacción"}
    {lang === "en" && "Register transaction"}
    {lang === "ru" && "Зарегистрировать транзакцию"}
    {lang === "zh" && "提交交易"}
  </button>
</form>

           </>
          )}

          {isPreLive && (
            <>
              <p className="text-slate-300 mb-10">{t.subtitlePre}</p>

              <form
                onSubmit={submit}
                className="max-w-md mx-auto bg-white/5 p-8 rounded-2xl shadow-[0_0_20px_#00E6FF30] text-left"
              >
                <label className="block mb-4">
                  <span className="text-sm">{t.name}</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2"
                  />
                </label>

                <label className="block mb-4">
                  <span className="text-sm">{t.email}</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2"
                  />
                </label>

                <label className="block mb-4">
                  <span className="text-sm">{t.language}</span>
                  <select
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      i18n.changeLanguage(e.target.value);
                    }}
                    className="mt-1 w-full rounded-lg bg-[#041a30] border border-white/20 px-4 py-2"
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="ru">Русский</option>
                    <option value="zh">中文</option>
                  </select>
                </label>

                <label className="flex items-start gap-3 mb-6 text-sm">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1"
                  />
                  <span>{t.consent}</span>
                </label>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full py-3 rounded-xl font-bold ${
                    canSubmit
                      ? "bg-[#00E6FF] text-[#022034]"
                      : "bg-gray-600 text-gray-300 cursor-not-allowed"
                  }`}
                >
                  {t.notify}
                </button>
              </form>
            </>
          )}

{isPresaleClosed && (
  <div className="mt-10 flex flex-col items-center gap-10">

    {/* Mensaje cierre */}
    <div className="text-center space-y-4">
      <p className="text-xl text-slate-300">
        {t.subtitleClosed}
      </p>

      <p className="text-lg font-semibold text-[#00E6FF]">
        {lang === "es" && "Puedes comprar tus NKTT en el pool oficial de Uniswap"}
        {lang === "en" && "You can buy your NKTT in the official Uniswap pool"}
        {lang === "ru" && "Вы можете купить NKTT в официальном пуле Uniswap"}
        {lang === "zh" && "你可以在官方 Uniswap 池中购买 NKTT"}
      </p>
    </div>

    {/* Uniswap + instrucciones */}
    <div className="flex flex-col lg:flex-row gap-10 items-start justify-center">

      {/* IFRAME UNISWAP */}
      <iframe
        src="https://app.uniswap.org/#/swap?chain=base&outputCurrency=0xdBfCb6BE6E1E42ab18a1A09931F8F8DAD90F99EF"
        width="420"
        height="600"
        style={{
          border: "0",
          borderRadius: "16px",
          boxShadow: "0 0 30px rgba(0,230,255,0.25)",
          backgroundColor: "#041a30",
        }}
        allow="clipboard-read; clipboard-write"
        loading="lazy"
        title="Uniswap Swap"
      />

      {/* INSTRUCCIONES */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-left shadow-[0_0_20px_#00E6FF20] max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {lang === "es" && "¿No ves tus NKTT en tu cartera?"}
          {lang === "en" && "Don’t see your NKTT in your wallet?"}
          {lang === "ru" && "Не видите свои NKTT в кошельке?"}
          {lang === "zh" && "在你的钱包中看不到 NKTT？"}
        </h2>

        <p className="text-slate-300 mb-4">
          {lang === "es" &&
            "Si has comprado NKTT pero no aparecen en tu cartera, sigue estos pasos:"}
          {lang === "en" &&
            "If you have purchased NKTT but they do not appear in your wallet, follow these steps:"}
          {lang === "ru" &&
            "Если вы приобрели NKTT, но они не отображаются в кошельке, выполните следующие шаги:"}
          {lang === "zh" &&
            "如果你已经购买了 NKTT 但钱包中未显示，请按照以下步骤操作："}
        </p>

        <ol className="list-decimal list-inside space-y-3 text-slate-300">
          <li>
            {lang === "es" &&
              "Abre tu cartera y busca la opción “Importar token” o “Añadir token”."}
            {lang === "en" &&
              "Open your wallet and look for the “Import token” or “Add token” option."}
            {lang === "ru" &&
              "Откройте кошелёк и найдите опцию «Импортировать токен» или «Добавить токен»."}
            {lang === "zh" &&
              "打开你的钱包，找到“导入代币”或“添加代币”的选项。"}
          </li>

          <li>
            {lang === "es" && "Selecciona la red Base si no está ya activa."}
            {lang === "en" && "Select the Base network if it is not already active."}
            {lang === "ru" && "Выберите сеть Base, если она ещё не активна."}
            {lang === "zh" && "如果尚未选择，请切换到 Base 网络。"}
          </li>

          <li>
            {lang === "es" &&
              "Introduce manualmente la dirección del contrato del token NKTT."}
            {lang === "en" &&
              "Manually enter the NKTT token contract address."}
            {lang === "ru" &&
              "Вручную введите адрес контракта токена NKTT."}
            {lang === "zh" &&
              "手动输入 NKTT 代币的合约地址。"}
          </li>

          <li className="font-mono break-all text-[#00E6FF]">
            0xdBfCb6BE6E1E42ab18a1A09931F8F8DAD90F99EF
          </li>

          <li>
            {lang === "es" &&
              "Confirma y tus NKTT aparecerán automáticamente."}
            {lang === "en" &&
              "Confirm and your NKTT will appear automatically."}
            {lang === "ru" &&
              "Подтвердите, и ваши NKTT появятся автоматически."}
            {lang === "zh" &&
              "确认后，你的 NKTT 将自动显示。"}
          </li>
        </ol>
      </div>
    </div>

    {/* Botón volver */}
    <a
      href={backToHome}
      className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 rounded-xl font-semibold hover:bg-white/10 transition shadow-[0_0_12px_#00E6FF40]"
    >
      ← {t.back}
    </a>
  </div>
)}
  
        </main>
      </div>
    </>
  );
}

